import React, { useState } from 'react';
import { SlipPayment, adminApi, BASE_URL } from '../services/api';
import { slipPaymentStatusLabel, orderStatusLabel } from '../lib/statusLabels';

interface Props { payment: SlipPayment; onBack: () => void; onDone: () => void; }

const SlipDetailPage: React.FC<Props> = ({ payment, onBack, onDone }) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<{ action: string; status: string } | null>(null);

  const slipLabel = slipPaymentStatusLabel[payment.status] ?? payment.status;
  const orderLabel = payment.order_status
    ? orderStatusLabel[payment.order_status] ?? payment.order_status
    : '—';

  const handle = async (action: 'approve' | 'reject') => {
    setLoading(true); setError('');
    try {
      const res = await adminApi.verify(payment.id, action, note);
      setResult({ action, status: res.status });
      setDone(true);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const fmt = (iso: string) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return 'ไม่ระบุเวลา';
      return d.toLocaleString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'ไม่ระบุเวลา';
    }
  };

  const orderExpired = payment.order_status === 'expired';
  const showActions = payment.status === 'submitted';

  return (
    <div style={{ maxWidth: 540, margin: '0 auto', width: '100%' }}>
      <button type="button" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#888', marginBottom: 20, padding: '10px 4px', minHeight: 44, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
        ← กลับรายการ
      </button>

      <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase', marginBottom: 2 }}>ตรวจสอบสลิป</p>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>รายละเอียดการชำระ</h2>

      {orderExpired && (
        <div style={{ borderRadius: 12, padding: '12px 16px', marginBottom: 16, background: '#fafaf9', border: '1.5px solid #d6d3d1', fontSize: 13, color: '#444' }}>
          <strong style={{ display: 'block', marginBottom: 4 }}>⌛ ออเดอร์หมดเวลาชำระ</strong>
          ไม่ควรอนุมัติสลิป — ให้ใช้ปฏิเสธเมื่อสลิปยังอยู่ในสถานะรอตรวจ (ฝั่ง API อาจยังอนุมัติได้ ขึ้นกับ logic บน server)
        </div>
      )}

      {/* Slip image */}
      <div style={{ borderRadius: 16, overflow: 'hidden', background: '#f9fafb', border: '1.5px solid #f3f4f6', marginBottom: 20, minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {payment.slip_url
          ? <img src={`${BASE_URL}/slip-image/${payment.id}`} alt="slip" style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }} />
          : <p style={{ color: '#aaa', fontSize: 14 }}>ไม่มีรูปสลิป</p>
        }
      </div>

      {/* Info */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', border: '1.5px solid #f3f4f6', marginBottom: 20 }}>
        {[
          ['Payment ID', payment.id],
          ['Order ID', payment.order_id],
          ['ชื่อ (LINE)', payment.line_display_name?.trim() || '—'],
          ['User ID', payment.user_id],
          ['สถานะสลิป', slipLabel],
          ['สถานะออเดอร์', orderLabel],
          ['เวลาอัปโหลด', fmt(payment.created_at)],
        ].map(([label, val]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14, alignItems: 'flex-start', gap: 12 }}>
            <span style={{ color: '#888', fontWeight: 500, flexShrink: 0 }}>{label}</span>
            <span style={{ fontWeight: 600, fontFamily: String(label).includes('ID') ? 'monospace' : 'inherit', wordBreak: 'break-all', textAlign: 'right', fontSize: 12 }}>{val}</span>
          </div>
        ))}
      </div>

      {done && result ? (
        <div style={{ borderRadius: 16, padding: '20px 24px', textAlign: 'center', background: result.action === 'approve' ? '#f0fff4' : '#fff0f0', border: `1.5px solid ${result.action === 'approve' ? '#86efac' : '#fca5a5'}` }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{result.action === 'approve' ? '✅' : '❌'}</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: result.action === 'approve' ? '#16a34a' : '#dc2626' }}>
            {result.action === 'approve' ? 'อนุมัติสำเร็จ — order เปลี่ยนเป็น paid + ได้รับ +50 แต้ม' : 'ปฏิเสธสำเร็จ'}
          </p>
          <button type="button" onClick={onDone} style={{ marginTop: 16, padding: '10px 28px', borderRadius: 40, border: 'none', background: '#1a0f0c', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            กลับรายการ
          </button>
        </div>
      ) : (
        <>
          {error && <div style={{ borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, background: '#fff0f0', color: '#c00', border: '1px solid #fca5a5' }}>{error}</div>}

          {showActions ? (
            <>
              {orderExpired && (
                <p style={{ fontSize: 13, color: '#78716c', marginBottom: 12, lineHeight: 1.5 }}>
                  ออเดอร์หมดเวลาแล้ว — ปุ่มอนุมัติถูกปิด แนะนำให้ปฏิเสธสลิป
                </p>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>หมายเหตุ (ถ้ามี)</label>
                <textarea
                  value={note} onChange={e => setNote(e.target.value)}
                  rows={2} placeholder="เช่น ยอดไม่ตรง, สลิปไม่ชัด..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 14, resize: 'vertical' }}
                />
              </div>
              <div className="admin-detail-actions">
                <button type="button" onClick={() => handle('reject')} disabled={loading}
                  style={{ padding: '14px', borderRadius: 50, border: '2px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, fontSize: 15, cursor: 'pointer', minHeight: 48, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
                  ❌ ปฏิเสธ
                </button>
                <button type="button" onClick={() => handle('approve')} disabled={loading || orderExpired}
                  title={orderExpired ? 'ออเดอร์หมดเวลาแล้ว' : undefined}
                  style={{ padding: '14px', borderRadius: 50, border: 'none', background: orderExpired ? '#d6d3d1' : 'linear-gradient(to right, #16a34a, #15803d)', color: orderExpired ? '#78716c' : '#fff', fontWeight: 700, fontSize: 15, cursor: orderExpired ? 'not-allowed' : 'pointer', minHeight: 48, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
                  ✅ อนุมัติ
                </button>
              </div>
            </>
          ) : (
            <div style={{ borderRadius: 12, padding: '14px 18px', background: '#f9fafb', border: '1.5px solid #f3f4f6', textAlign: 'center', color: '#888', fontSize: 14 }}>
              สลิปนี้ดำเนินการแล้ว ({slipLabel})
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SlipDetailPage;
